import { useRouter } from "next/router";
import React from "react";
import Footer from "../components/Footer";
import Loading from "../components/Loading";
import NavBar from "../components/TeamsComponents/Navbar";
import Script from "next/script";
import ScoutBar from "../components/ScoutBar";
import useTeam from "../hooks/useTeam";
import useSnackbar from "../hooks/useSnackbar";
import useUser from "../hooks/useUser";
import { BiInfoCircle } from "react-icons/bi";
import { HiSpeakerphone } from "react-icons/hi";
import { MdDangerous } from "react-icons/md";
import Link from "next/link";
import { setCookie } from "cookies-next";
import { differenceInDays, format } from "date-fns";
import OnboardingCycle from "../components/onboarding/OnboardingCycle";
import { handleRenewSubscription } from "../backend/subscription";

const TeamLayout = ({ children }: { children: React.ReactNode }) => {
  const [onboarding, setOnboarding] = React.useState<boolean | null>(false);
  const router: any = useRouter();
  const teamName = router.query.team as string;
  const { user } = useUser();
  const [renewSubscription, setRenewSubscription] = React.useState(false);
  const { error, loading, team } = useTeam(teamName);
  const { close, setSnackbar } = useSnackbar();

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      const onboardTeam =
        team?.subscription?.status === "active" &&
        !localStorage.getItem(`${team?.id}-teamonboarding`);
      setOnboarding(onboardTeam);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  React.useEffect(() => {
    if (!team || !user || loading) return;
    const today = new Date();
    const expiryDate = new Date(team?.subscription?.expiry_date || today);
    const daysRemaining = differenceInDays(expiryDate, today);
    const isDowngraded: boolean = team?.subscription?.downgraded;
    const creator = team.members?.find(
      (member: any) => member.role === "CREATOR",
    );

    const formattedDate = format(expiryDate, "do MMMM yyyy");
    const subject = `Payment For ${team?.name} due at ${formattedDate}`;
    const mailtoLink = `mailto:${creator?.email}?subject=${subject}`;

    if (isDowngraded) {
      setSnackbar({
        content: (
          <div className="flex items-center space-x-2 text-base font-light">
            <span className="rounded-full bg-warning p-1 text-white">
              <BiInfoCircle size={16} />
            </span>
            <span className="font-medium">
              Your plan has been scheduled for a downgrade. You will lose access
              to paid features at the end of your current billing cycle.
            </span>
          </div>
        ),
        type: "warning",
        open: true,
      });
    } else {
      setSnackbar({
        content: (
          <div className="flex items-center justify-between space-x-2 pr-10 text-base font-light">
            {daysRemaining < 1 ? (
              <>
                <div className="flex flex-row items-center space-x-2">
                  <span className="rounded-full bg-danger p-1 text-white">
                    <MdDangerous size={16} />
                  </span>
                  <p className="text-sm md:text-base">
                    Hey {team.name}, your subscription has expired.
                  </p>
                  <Link href={"#"}>
                    <a className="hidden text-sm font-medium underline lg:block lg:text-base">
                      What does this mean?
                    </a>
                  </Link>
                </div>
                <div className="hidden flex-row items-center space-x-5 md:flex">
                  {!team.isCreator ? (
                    <a
                      href={mailtoLink}
                      rel="noreferrer"
                      target="_blank"
                      className="btn rounded-md border-none bg-white px-4 font-medium text-black hover:border-none hover:bg-white"
                    >
                      Contact Admin
                    </a>
                  ) : (
                    <button
                      onClick={() =>
                        handleRenewSubscription(setRenewSubscription, {
                          invoiceId: team?.subscription._id,
                        })
                      }
                      disabled={renewSubscription}
                      className="btn rounded-md border-none bg-white px-4 font-medium text-black hover:border-none hover:bg-white"
                    >
                      Renew subscription
                    </button>
                  )}
                </div>
              </>
            ) : daysRemaining >= 1 && daysRemaining <= 4 ? (
              <>
                <div className="flex flex-row items-center space-x-2">
                  <span className="rounded-full bg-warning p-1 text-white">
                    <BiInfoCircle size={16} />
                  </span>

                  {team?.isCreator ? (
                    <p className="text-sm md:text-base">
                      Hey {team.name}, your subscription expires in{" "}
                      <span className="font-semibold text-warning">
                        {daysRemaining} {daysRemaining > 1 ? "days" : "day"}
                      </span>
                    </p>
                  ) : (
                    <>
                      <p className="hidden text-xs md:block md:text-base">
                        Hey {team?.name}, don&apos;t wait any longer. Contact
                        your admin as your subscription expires in{" "}
                        <span className="font-semibold text-info">
                          {daysRemaining} {daysRemaining > 1 ? "days" : "day"}
                        </span>
                      </p>
                      <p className="block text-xs md:hidden md:text-base">
                        Hey {team?.name}, your subscription expires in{" "}
                        <span className="font-semibold text-info">
                          {daysRemaining} {daysRemaining > 1 ? "days" : "day"}
                        </span>
                      </p>
                    </>
                  )}
                </div>
                <div className="hidden flex-row items-center space-x-5 text-sm font-medium md:flex">
                  {!team.isCreator ? (
                    <button className="btn rounded-md border-none bg-white px-4 font-medium text-black hover:border-none hover:bg-white">
                      Contact Admin
                    </button>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2">
                  <span className="flex items-center justify-center rounded-full bg-primary p-2 text-white">
                    <HiSpeakerphone size={16} />
                  </span>
                  {team?.isCreator ? (
                    <p className="text-sm font-medium md:text-base">
                      Hey {team?.name}, your subscription expires in{" "}
                      <span className="font-semibold text-info">
                        {daysRemaining} days
                      </span>
                    </p>
                  ) : (
                    <>
                      <p className="hidden text-xs font-medium md:block md:text-base">
                        Hey {user?.first_name}, get in touch with your admin as
                        your subscription expires in{" "}
                        <span className="font-semibold text-info">
                          {daysRemaining} days!
                        </span>
                      </p>
                      <p className="block text-xs font-medium md:hidden md:text-base">
                        Hey {user?.first_name}, your subscription expires in{" "}
                        <span className="font-semibold text-info">
                          {daysRemaining} days!
                        </span>
                      </p>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        ),
        type:
          daysRemaining < 1
            ? "danger"
            : daysRemaining >= 1 && daysRemaining <= 4
              ? "warning"
              : "info",
        open: daysRemaining < 15,
      });
    }

    return () => {
      close();
    };
  }, [team, user, loading]);

  React.useEffect(() => {
    if (error) {
      router.push("/overview");
    }

    if (team?.id) {
      setCookie(`${team?.name}-sub`, {
        name: team?.name,
        id: team?.id,
        status: team?.subscription?.status || "inactive",
      });
    }
    if (
      team?.id &&
      team?.subscription?.status !== "active" &&
      !router.pathname.includes("/suspended")
    ) {
      if (!router.pathname.includes("/settings")) {
        router.replace(`/teams/${teamName}/suspended`);
      }
    }
  }, [error, router, team]);

  // if (loading || error) {
  //   return (
  //     <main className="container flex items-center justify-center min-h-screen pb-20 md:pb-0">
  //       <Loading loading={true} />
  //     </main>
  //   );
  // }

  if (loading || error) {
    return <Loading loading={true} progressDuration={5000} />;
  }

  return (
    <>
      <Script id="spotlight" strategy="afterInteractive">{`
        document.addEventListener("keydown", function(event) {
          if (event.ctrlKey && event.key === "k") {
            event.preventDefault();
          }
        })
      `}</Script>

      <ScoutBar />
      <NavBar />
      {onboarding && <OnboardingCycle team={team} />}
      <main className="container mx-auto flex min-h-screen max-w-7xl flex-col px-4 pb-20">
        {children}
      </main>
      <Footer />
    </>
  );
};

export default TeamLayout;
